import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MatSortDirection } from '@fi-shared/types';
import { sortArrayByProperty } from '@fi-shared/utils/array-sort-comparators';
import { isDefined, isEqual } from '@fi-shared/utils';
import {
    IChecklist2ForReport,
    IChecklist2ReportAnswer,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { FiDeviationChecklistDetailsTable } from '../../../components/deviations-report/deviation-checklist-details-table-wrapper/deviation-checklist-details-table/deviation-checklist-details-table.component';

@Component({
    selector: 'fi-deviation-checklist-details-table-wrapper',
    template: `
        @let loading = loading$ | async;
        @if (loading) {
            <div class="text-center">
                <fi-loading class="small" />
            </div>
        } @else {
            <fi-deviation-checklist-details-table
                #fiDeviationChecklistDetailsTable
                [isDeviationBadgeShown]="isDeviationBadgeShown"
                [newDeviationAnswerIds]="newDeviationAnswerIds"
                [availableChecklists]="availableChecklists"
                [deviationItems]="filteredAnswerDetails$ | async"
                [isChecklistNameShown]="isChecklistNameShown"
                [isCheckpointNameShown]="isCheckpointNameShown"
                [isScoringShown]="isScoringShown"
                (onTableSort)="sortDeviationsList()"
                (onOpenConversation)="onOpenConversation()"
            />
        }
    `,
    styleUrls: ['./deviation-checklist-details-table-wrapper.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-deviation-checklist-details-table-wrapper' },
    standalone: false,
})
export class FiDeviationChecklistDetailsTableWrapperComponent implements OnInit, OnChanges {
    @Input() answerDetails: IChecklist2ReportAnswer[] = [];
    @Input() isDeviationBadgeShown: boolean;
    @Input() newDeviationAnswerIds: number[] = [];
    @Input() availableChecklists: IChecklist2ForReport[] = [];
    @Input() isChecklistNameShown: boolean = true;
    @Input() isCheckpointNameShown: boolean = true;
    @Input() isScoringShown: boolean = true;

    @Output() conversationOpened = new EventEmitter<void>();

    @ViewChild('fiDeviationChecklistDetailsTable') fiDeviationChecklistDetailsTable: FiDeviationChecklistDetailsTable;

    loading$ = new BehaviorSubject<boolean>(false);
    filteredAnswerDetails$ = new BehaviorSubject<IChecklist2ReportAnswer[]>([]);

    private readonly _automatedTaskColumnName: string = 'AutomatedTask';

    constructor(private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService) {}

    ngOnInit() {
        this.filteredAnswerDetails$.next(this.answerDetails);
    }

    ngOnChanges(changes: SimpleChanges) {
        const change = changes.answerDetails;
        if (!!change && !isEqual(change.currentValue, change.previousValue)) {
            this.filteredAnswerDetails$.next(this.answerDetails);
            this.sortDeviationsList();
        }
    }

    sortDeviationsList() {
        if (!isDefined(this.fiDeviationChecklistDetailsTable)) return;

        const sortColumnName: string = this.fiDeviationChecklistDetailsTable.selectedSort.active;
        const isDescendingSort: boolean =
            this.fiDeviationChecklistDetailsTable.selectedSort.direction === MatSortDirection.Descending;

        if (sortColumnName !== this._automatedTaskColumnName) return;

        this.filteredAnswerDetails$.next(
            sortArrayByProperty(
                this.filteredAnswerDetails$.value,
                this.sortAutomatedTaskColumnFn.bind(this),
                isDescendingSort,
            ),
        );
    }

    private sortAutomatedTaskColumnFn(item: IChecklist2ReportAnswer): boolean {
        return (
            this.fiChecklist2ReportHelperService.isAutomatedActionAdded(item) ||
            this.fiChecklist2ReportHelperService.isOneTimeTaskAdded(item)
        );
    }

    onOpenConversation() {
        this.conversationOpened.emit();
    }
}
